import TagManager from 'react-gtm-module';
import { GtmUserCookieType } from 'types/GtmUserCookie';
import {
  isCategoryPage,
  isCheckoutPage,
  isDirectOrderPage,
  isProductSheet,
  isSearchPage,
} from './validation';
import { OrderConfirmationData } from 'types/Order';

export function getDataLayer(dataLayerData: {
  pageName: string;
  event: string;
  eventCat?: string;
  eventAct?: string;
  eventLab?: string;
  eventCommand?: string;
  currencyCode?: string;
  productImpressionListName?: string;
  productList?: GtmProductListType;
  searchKeywords?: string;
  searchResultsNumber?: number;
  promotionList?: GtmPromotionType;
  checkoutStepNumber?: number;
  checkoutStepName?: string;
  orderConfirmationData?: OrderConfirmationData;
}): dataLayerDataType {
  const {
    pageName,
    event,
    eventCat,
    eventAct,
    eventLab,
    eventCommand,
    currencyCode,
    productImpressionListName,
    productList,
    promotionList,
    checkoutStepNumber,
    checkoutStepName,
    orderConfirmationData,
  } = dataLayerData;

  const dataLayer = {
    page_name: pageName,
    page_type: getPageType(pageName),
    event,
    ...(eventCat && { eventCat }),
    ...(eventAct && { eventAct }),
    ...(eventLab && { eventLab }),
  };

  return {
    ...(currencyCode && { currencyCode: currencyCode }),
    ...(checkoutStepNumber && { checkoutStepNumber: checkoutStepNumber }),
    ...(checkoutStepName && { checkoutStepName: checkoutStepName }),
    ...(orderConfirmationData &&
      orderConfirmationData.order_id && {
        order_id: orderConfirmationData.order_id,
      }),
    ...(orderConfirmationData &&
      orderConfirmationData.order_revenue && {
        order_revenue: orderConfirmationData.order_revenue,
      }),
    ...(orderConfirmationData &&
      orderConfirmationData.order_tax && {
        order_tax: orderConfirmationData.order_tax,
      }),
    ...(orderConfirmationData &&
      orderConfirmationData.order_shipping && {
        order_shipping: orderConfirmationData.order_shipping,
      }),
    ...(orderConfirmationData &&
      orderConfirmationData.order_shipping && {
        order_shipping: orderConfirmationData.order_shipping,
      }),
    ...(orderConfirmationData &&
      orderConfirmationData.order_payment_method && {
        order_payment_method: orderConfirmationData.order_payment_method,
      }),
    ...(orderConfirmationData &&
      orderConfirmationData.order_shipping_method && {
        order_shipping_method: orderConfirmationData.order_shipping_method,
      }),
    ...(orderConfirmationData &&
      orderConfirmationData.order_discount_ati && {
        order_discount_ati: orderConfirmationData.order_discount_ati,
      }),
    ...(orderConfirmationData &&
      orderConfirmationData.revenue_tf_without_sf && {
        revenue_tf_without_sf: orderConfirmationData.revenue_tf_without_sf,
      }),
    ...(orderConfirmationData &&
      orderConfirmationData.order_coupon && {
        order_coupon: orderConfirmationData.order_coupon,
      }),
    ...(orderConfirmationData &&
      orderConfirmationData.order_type && {
        order_type: orderConfirmationData.order_type,
      }),
    ...(orderConfirmationData &&
      orderConfirmationData.order_status && {
        order_status: orderConfirmationData.order_status,
      }),
    ...(orderConfirmationData &&
      orderConfirmationData.customer_status && {
        customer_status: orderConfirmationData.customer_status,
      }),
    ...(productImpressionListName && {
      productImpressionListName: productImpressionListName,
    }),
    ...(productList && { productList: productList }),
    ...(promotionList && { promotionList: promotionList }),
    ...(eventCommand && { eventCommand: eventCommand }),
    ...dataLayer,
  };
}

export function gtmAfiEventLab(
  pathname: string,
  event: string,
  eventCat: string,
  eventAct: string,
  gtmUserCookie?: GtmUserCookieType,
  eventLab?: string
): void {
  const data = {
    pageName: pathname,
    event,
    eventCat: eventCat,
    eventAct,
    eventLab,
  };
  TagManager.dataLayer({
    dataLayer: getDataLayer(eventLab ? data : data),
  });
}

function getPageType(url: string): string {
  if (isProductSheet(url)) {
    return 'product';
  }
  if (isCategoryPage(url)) {
    return 'productList';
  }
  if (isSearchPage(url)) {
    return 'searchPage';
  }
  if (isCheckoutPage(url)) {
    return 'funnel';
  }
  if (isDirectOrderPage(url)) {
    return 'funnel';
  }
  return 'homepage';
}

type dataLayerDataType = {
  event: string;
  eventCat?: string;
  eventAct?: string;
  eventLab?: string;
  eventCommand?: string;
  currencyCode?: string;
};

type GtmProductType = {
  product_name: string;
  product_id: string;
  product_unitprice_ati: string;
  product_discount_ati?: string;
  promo_discount?: string | null;
  product_brand?: string;
  product_color?: string | null;
  product_size?: string | null;
  product_rating?: string;
  product_instock?: string;
  product_category1: string | null;
  product_category2: string | null;
  product_category3: string | null;
  product_category4: string | null;
  product_quantity?: number | null;
  productImpressionListPosition?: number;
};

type GtmFilteredProductType = {
  product_id: string;
};

export type GtmProductListType = GtmProductType[] | GtmFilteredProductType[];

type GtmPromotionType = {
  pageName?: string;
  eventCommand?: string;
  promotionName?: string;
  promotionId?: string;
  promotionCreative?: string;
  promotionPosition?: string;
};
